<template>
	<div>
		<!-- Summary Cards -->
		<div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
			<div class="p-4 rounded-lg bg-green-50 shadow-sm border border-green-200">
				<span class="text-lg font-bold text-green-700">
					€ {{ formatNumber(data.revenue) }}
				</span>
				<p class="text-xs text-green-600 mt-1">Total Revenue (Stripe)</p>
			</div>

			<div class="p-4 rounded-lg bg-red-50 shadow-sm border border-red-200">
				<span class="text-lg font-bold text-red-700">
					€ {{ formatNumber(data.payout) }}
				</span>
				<p class="text-xs text-red-600 mt-1">Total Payouts</p>
			</div>

			<div class="p-4 rounded-lg bg-gray-50 shadow-sm border border-gray-200">
				<span class="text-lg font-bold"
					:class="netLoss > 0 ? 'text-green-700' : netLoss < 0 ? 'text-red-700' : 'text-gray-700'">
					€ {{ formatNumber(netLoss) }}
				</span>
				<p class="text-xs mt-1"
					:class="netLoss > 0 ? 'text-green-600' : netLoss < 0 ? 'text-red-600' : 'text-gray-600'">
					{{ netLoss > 0 ? 'Net Profit' : netLoss < 0 ? 'Net Loss' : 'Break Even' }} </p>
			</div>
		</div>

		<!-- Revenue vs Payout -->
		<div class="mt-6">
			<h4 class="text-sm font-medium text-gray-700 mb-2">
				Revenue vs Payout Comparison
			</h4>

			<!-- Dynamic percentage text -->
			<div class="flex items-center space-x-2 text-sm mb-1"
				:class="revenuePercent >= 0 ? 'text-green-600' : 'text-red-600'">
				<span>
					{{ revenuePercent >= 0 ? `+${revenuePercent.toFixed(1)}% Revenue` : `${revenuePercent.toFixed(1)}%
					Revenue` }}
				</span>
			</div>

			<!-- Comparison bar -->
			<div class="h-2 bg-gray-200 rounded-full relative overflow-hidden">
				<div class="h-2 rounded-full transition-all duration-500"
					:class="revenuePercent >= 0 ? 'bg-green-500' : 'bg-red-500'"
					:style="{ width: `${Math.min(Math.abs(revenuePercent), 100)}%` }"></div>
			</div>

			<!-- Values -->
			<div class="flex justify-between text-xs mt-2 text-gray-500">
				<span>Revenue: € {{ formatNumber(data.revenue) }}</span>
				<span>Payouts: € {{ formatNumber(data.payout) }}</span>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
	data: {
		type: Object,
		required: true,
		default: () => ({ revenue: 0, payout: 0 }),
	},
});

// Format numbers with commas
const formatNumber = (value) => {
	return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value || 0);
};

// Compute revenue percentage difference
const revenuePercent = computed(() => {
	const payout = props.data.payout || 0;
	const revenue = props.data.revenue || 0;
	if (payout === 0) return 0;
	return ((revenue - payout) / payout) * 100;
});

// Net profit/loss
const netLoss = computed(() => (props.data.revenue || 0) - (props.data.payout || 0));
</script>
